import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { delay, map, switchMap, tap, catchError } from 'rxjs/operators';
import {
  Sale,
  SaleItem,
  SaleStatus,
  Cart,
  StockIssue,
} from '../models/kiosk.models';
import { ProductService } from './product.service';
import { environment } from '../../../environments/environment';
import { LogService } from '../../services/log.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class SalesService {
  private http = inject(HttpClient);
  private ngZone = inject(NgZone);
  private log = inject(LogService);

  private salesHistory: Sale[] = [];
  private nextSaleId = 1001;
  private _sales = signal<Sale[]>([]);
  readonly salesSignal = this._sales.asReadonly();
  /** @deprecated Use salesSignal signal instead */
  readonly sales$ = toObservable(this.salesSignal);
  private loaded = false;

  // Subject pour notifier les changements (nouvelle vente créée)
  private salesChangedSubject = new Subject<void>();
  public salesChanged$ = this.salesChangedSubject.asObservable();

  // EventSource pour SSE
  private eventSource: EventSource | null = null;

  constructor(private productService: ProductService) {
    // Charger l'historique depuis le backend
    this.loadSalesFromBackend();
    // Connecter au SSE pour les mises à jour en temps réel
    this.connectToSSE();
  }

  createSale(
    cart: Cart,
    serviceId?: string,
  ): Observable<{
    success: boolean;
    sale?: Sale;
    stockIssues?: StockIssue[];
    message: string;
  }> {
    const saleItems: SaleItem[] = [];
    const timestamp = new Date();

    // Convertir les items du panier en items de vente
    // Les stocks ont déjà été décrémentés lors de l'ajout au panier
    cart.items.forEach((cartItem, itemId) => {
      saleItems.push({
        productId: cartItem.product.id,
        productName: cartItem.product.name,
        quantity: cartItem.quantity,
        unitPrice: cartItem.product.price,
        totalPrice: cartItem.product.price * cartItem.quantity,
        isReplacement: cartItem.isReplacement || false,
        originalProductId: cartItem.originalProductId,
      });
    });

    if (saleItems.length === 0) {
      return of({
        success: false,
        message: 'Aucun produit dans le panier',
      });
    }

    const totalAmount = saleItems.reduce(
      (sum, item) => sum + item.totalPrice,
      0,
    );

    const sale: Sale = {
      id: this.nextSaleId++,
      saleNumber: `VK${timestamp.getFullYear()}${String(timestamp.getMonth() + 1).padStart(2, '0')}${String(timestamp.getDate()).padStart(2, '0')}-${String(this.nextSaleId).padStart(4, '0')}`,
      timestamp,
      items: saleItems,
      totalAmount,
      status: SaleStatus.COMPLETED,
    };

    // Préparer les données pour le backend (format DailySales)
    const payload: any = {
      products: saleItems.map((item) => ({
        productId: item.productId,
        productName: item.productName,
        category: 'N/A',
        unitPrice: item.unitPrice,
        quantitySold: item.quantity,
        quantityPrepared: item.quantity,
        quantityWasted: 0,
        revenue: item.totalPrice,
        wasteAmount: 0,
        hadRupture: false,
      })),
      summary: {
        totalRevenue: totalAmount,
        totalQuantitySold: saleItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        ),
        totalQuantityPrepared: saleItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        ),
      },
      paymentMethod: 'cash',
      notes: saleItems.some((i) => i.isReplacement)
        ? 'Contient des substitutions'
        : undefined,
      source: 'kiosk',
    };

    // Ajouter le serviceId si fourni
    if (serviceId) {
      payload.serviceId = serviceId;
    }

    // Si mockApi est activé, simuler la création en local sans appel HTTP
    if (environment.mockApi) {
      this.log.debug('Mode Mock - Création de vente en local');
      this.salesHistory.unshift(sale);
      this._sales.set([...this.salesHistory]);
      this.salesChangedSubject.next(); // Notifier le changement
      return of({
        success: true,
        sale,
        message: 'Vente enregistrée (mode mock)',
      }).pipe(delay(300)); // Simuler un délai réseau
    }

    // Appel HTTP pour enregistrer la vente au backend
    return this.http
      .post<{
        success: boolean;
        data?: any;
        message?: string;
      }>(`${environment.apiUrl}/sales`, payload)
      .pipe(
        map((response) => {
          if (response.success) {
            // Utiliser les données du backend si disponibles, sinon notre sale local
            const savedSale = response.data
              ? {
                  ...sale,
                  id: response.data._id || sale.id,
                }
              : sale;

            this.salesHistory.unshift(savedSale);
            this._sales.set([...this.salesHistory]);
            this.salesChangedSubject.next(); // Notifier le changement

            return {
              success: true,
              sale: savedSale,
              message: response.message || 'Vente enregistrée',
            };
          }
          return {
            success: false,
            message:
              response.message || 'Erreur lors de la création de la vente',
          };
        }),
        catchError((error) => {
          // Si hors ligne ou erreur réseau, retourner succès avec vente locale
          // Le OfflineQueueService gèrera la synchronisation
          if (!navigator.onLine || error.status === 0) {
            this.log.info(
              'Mode hors ligne - Vente gérée par OfflineQueueService',
            );

            // Ajouter quand même la vente à l'historique local pour affichage immédiat
            this.salesHistory.unshift(sale);
            this._sales.set([...this.salesHistory]);
            this.salesChangedSubject.next(); // Notifier le changement

            return of({
              success: true,
              sale,
              message:
                '✅ Vente enregistrée (sera synchronisée automatiquement)',
            });
          }

          // Autres erreurs - les propager
          throw error;
        }),
      );
  }

  /**
   * Charge les ventes depuis le backend ou utilise mode mock selon environment.mockApi
   */
  private loadSalesFromBackend(): void {
    if (this.loaded) return;

    // Si mockApi est activé, ne pas charger depuis le backend
    if (environment.mockApi) {
      this.log.debug('Mode Mock activé - Historique des ventes vide en local');
      this.salesHistory = [];
      this._sales.set([]);
      this.loaded = true;
      return;
    }

    // Sinon, charger depuis le backend
    this.log.debug('Chargement des ventes depuis le backend');
    this.http
      .get<{ success: boolean; data: any[]; total?: number }>(
        `${environment.apiUrl}/sales`,
        {
          params: {
            limit: '100',
            skip: '0',
          },
        },
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Convertir les ventes DailySales du backend au format local
            this.salesHistory = response.data.map((backendSale, index) => {
              const saleProducts = backendSale.products || [];

              return {
                id: backendSale._id || 1001 + index,
                saleNumber: `VK${new Date(backendSale.saleDate || backendSale.createdAt).getFullYear()}${String(new Date(backendSale.saleDate || backendSale.createdAt).getMonth() + 1).padStart(2, '0')}${String(new Date(backendSale.saleDate || backendSale.createdAt).getDate()).padStart(2, '0')}-${String(index + 1).padStart(4, '0')}`,
                timestamp: new Date(
                  backendSale.saleDate || backendSale.createdAt,
                ),
                items: saleProducts.map((product: any) => ({
                  productId: product.productId,
                  productName: product.name,
                  quantity: product.sold,
                  unitPrice: product.price,
                  totalPrice: product.revenue,
                  isReplacement: false,
                })),
                totalAmount: backendSale.summary?.totalRevenue || 0,
                status:
                  backendSale.status === 'completed' ||
                  backendSale.validationStatus === 'validated'
                    ? SaleStatus.COMPLETED
                    : backendSale.status === 'cancelled'
                      ? SaleStatus.CANCELLED
                      : SaleStatus.PENDING,
              };
            });

            // Calculer le prochain ID
            if (this.salesHistory.length > 0) {
              const maxId = Math.max(
                ...this.salesHistory.map((s) =>
                  typeof s.id === 'number' ? s.id : 0,
                ),
              );
              this.nextSaleId = maxId + 1;
            }

            this._sales.set([...this.salesHistory]);
            this.log.debug(
              `✅ ${this.salesHistory.length} ventes chargées depuis le backend`,
            );
            this.loaded = true;
          }
        },
        error: (error) => {
          this.log.error('❌ Erreur lors du chargement des ventes:', error);
          this.log.warn(
            '⚠️ Historique des ventes vide - Vérifiez que le backend est démarré',
          );
          // Continuer avec un historique vide en cas d'erreur
          this.loaded = true;
        },
      });
  }

  /**
   * Recharge les ventes depuis le backend
   */
  refreshSales(): Observable<Sale[]> {
    this.loaded = false;
    this.loadSalesFromBackend();
    return this.sales$;
  }

  getSalesHistory(limit?: number): Observable<Sale[]> {
    // Si pas encore chargé, attendre le chargement
    if (!this.loaded) {
      return this.sales$.pipe(
        map((sales) => (limit ? sales.slice(0, limit) : sales)),
      );
    }
    const sales = limit ? this.salesHistory.slice(0, limit) : this.salesHistory;
    return of(sales);
  }

  getSalesByService(
    startDate?: string,
    endDate?: string,
    limit?: number,
  ): Observable<any> {
    let params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (limit) params.limit = limit;

    return this.http
      .get<{
        success: boolean;
        data: any[];
      }>(`${environment.apiUrl}/sales/by-service`, { params })
      .pipe(map((response) => response.data));
  }

  getSaleById(id: number | string): Observable<Sale | undefined> {
    return this.sales$.pipe(map((sales) => sales.find((s) => s.id === id)));
  }

  getTodaySales(): Observable<Sale[]> {
    return this.sales$.pipe(
      map((sales) => {
        const now = new Date();
        const todayStr = now.toISOString().split('T')[0]; // Format YYYY-MM-DD

        this.log.debug('🔍 getTodaySales - Total sales:', sales.length);
        this.log.debug('🔍 getTodaySales - Looking for date:', todayStr);

        const todaySales = sales.filter((sale) => {
          const saleDate = new Date(sale.timestamp);
          const saleDateStr = saleDate.toISOString().split('T')[0]; // Format YYYY-MM-DD
          this.log.debug('🔍 Sale:', sale.saleNumber, 'Date:', saleDateStr);
          const isToday = saleDateStr === todayStr;
          this.log.debug('🔍 Is today?', isToday);
          return isToday;
        });

        this.log.debug(
          '✅ getTodaySales - Filtered today sales:',
          todaySales.length,
        );
        return todaySales;
      }),
    );
  }

  private connectToSSE(): void {
    // ⚠️ SSE désactivé temporairement (problème HTTP/2 avec Caddy)
    // Voir docs/FEATURES.md section "Server-Sent Events" pour réactiver
    this.log.debug(
      'ℹ️ SSE désactivé - mises à jour temps réel non disponibles',
    );
    return;

    /* DÉSACTIVÉ - Pour réactiver, supprimer le return et ce commentaire
    // Ne pas se connecter au SSE en mode mock
    if (environment.mockApi) {
      this.log.debug('🎭 Mode Mock - SSE désactivé');
      return;
    }

    // Récupérer le token d'authentification
    const token = sessionStorage.getItem('token');
    if (!token) {
      this.log.warn('⚠️ Pas de token - connexion SSE différée');
      return;
    }

    try {
      // Créer la connexion EventSource avec le token dans l'URL
      const url = `${environment.apiUrl}/sales/stream?token=${token}`;
      this.eventSource = new EventSource(url);

      this.eventSource.onopen = () => {
        this.log.debug('✅ Connecté au SSE pour les mises à jour temps réel');
      };

      this.eventSource.onmessage = (event) => {
        this.log.debug('📩 Message SSE reçu:', event.data);
        try {
          const message = JSON.parse(event.data);
          this.log.debug('📩 Message SSE parsé:', message);
          
          if (message.type === 'sale-created') {
            this.log.debug('📡 Nouvelle vente reçue via SSE - émission notification');
            // Exécuter dans la zone Angular pour la détection de changement
            this.ngZone.run(() => {
              this.salesChangedSubject.next();
            });
          } else if (message.type === 'connected') {
            this.log.debug('✅ Confirmation connexion SSE');
          }
        } catch (error) {
          this.log.error('Erreur parsing message SSE:', error);
        }
      };

      this.eventSource.onerror = (error) => {
        this.log.error('❌ Erreur connexion SSE:', error);
        // Fermer et réessayer après 5 secondes
        this.eventSource?.close();
        setTimeout(() => this.connectToSSE(), 5000);
      };
    } catch (error) {
      this.log.error('❌ Erreur création EventSource:', error);
    }
    // FIN DU BLOC DÉSACTIVÉ */
  }

  ngOnDestroy(): void {
    // Fermer la connexion SSE
    if (this.eventSource) {
      this.eventSource.close();
      this.log.debug('🔌 Connexion SSE fermée');
    }
  }
}
